import React, { useState, useContext } from "react";
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Tooltip,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import CustomStepper from "../../../../custom-components/CustomStepper";
import {
  getStepperSteps,
  getStepIndex,
  getNextStep,
} from "../../../../../utils/stepperConfig";
import { playwrightTypeScriptTestFilesAPI } from "../../../../../services/webServices";
import { LoadingModalContext } from "../../../../main-pages/ServicePage";
import MessagePopup from "../../../../popup-components/MessagePopup";
import UploadTestDataDrawer from "./UploadTestDataDrawer";
import { ReactComponent as UploadBlueIcon } from "../../../../../images/test-automation/uploadIcon_bluecolour.svg";

// Utility functions to handle JSON array format
const convertPageClassesArrayToString = (pageClassesArray) => {
  if (!Array.isArray(pageClassesArray)) {
    return "// No page classes generated";
  }

  if (pageClassesArray.length === 0) {
    return "// No page classes generated";
  }

  return pageClassesArray
    .map((fileObj) => {
      const filePath = Object.keys(fileObj)[0];
      const content = fileObj[filePath];
      return `// ${filePath}\n${content}\n`;
    })
    .join("\n");
};

const convertPageClassesStringToArray = (pageClassesString) => {
  if (!pageClassesString || typeof pageClassesString !== "string") {
    return [];
  }

  const fileSections = pageClassesString.split(/\/\/ src\/pages\/[^\n]+/);
  const fileComments =
    pageClassesString.match(/\/\/ src\/pages\/[^\n]+/g) || [];

  const result = [];
  for (let i = 0; i < fileComments.length; i++) {
    const filePath = fileComments[i].replace("// ", "");
    const content = fileSections[i + 1]?.trim() || "";

    if (filePath && content) {
      result.push({ [filePath]: content });
    }
  }

  return result;
};

const isValidPageClassesArray = (response) => {
  return (
    Array.isArray(response) &&
    response.length > 0 &&
    response.every(
      (item) =>
        typeof item === "object" &&
        Object.keys(item).length === 1 &&
        Object.keys(item)[0].includes("src/pages/")
    )
  );
};

const GeneratePageObjectsPage = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [projectId, setProjectId] = useState(null);

  // Generate project ID function
  const generateProjectId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `proj_${timestamp}_${random}`;
  };

  // Generate project ID once when component mounts (session-based)
  React.useEffect(() => {
    if (!projectId) {
      const newProjectId = generateProjectId();
      setProjectId(newProjectId);
      console.log("🆔 Generated session project ID:", newProjectId);
    }
  }, []); // Empty dependency array - runs once on mount
  const { setLoadingState } = useContext(LoadingModalContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Get data from previous step
  const {
    automationType,
    preferences,
    environment,
    testCases,
    currentTestCaseIndex = 0,
    totalTestCases = 35,
    currentStep,
    currentStepIndex: stateStepIndex,
    generatedLocators,
    generatedPageObjects,
    pageSource,
  } = location.state || {};

  // Get current test case data
  const currentTestCase = testCases?.[currentTestCaseIndex] || {
    testCaseId: "TCD_FT_07_NEG",
    title: "Invalid Country Code",
    testSteps:
      "Attempt to retrieve weather data using a valid city name but an invalid country code.",
    expectedResult: "Regular text column",
  };

  // Initialize edited content
  React.useEffect(() => {
    if (!editedContent) {
      // Check if generatedPageObjects is a valid JSON array format
      if (
        generatedPageObjects &&
        isValidPageClassesArray(generatedPageObjects)
      ) {
        // Convert JSON array to string format for display
        const contentToUse =
          convertPageClassesArrayToString(generatedPageObjects);
        setEditedContent(contentToUse);
        console.log(
          `✅ Loaded ${generatedPageObjects.length} page classes from JSON array format`
        );
      } else {
        // Handle legacy string format or no data
        const contentToUse =
          generatedPageObjects ||
          "// No page objects generated yet. Please generate page objects first.";
        setEditedContent(contentToUse);
        console.log("📝 Using legacy string format or no data");
      }
    }
  }, [generatedPageObjects]);

  // Get dynamic steps based on framework selection
  const progressSteps = getStepperSteps(preferences);
  const currentStepIndex =
    stateStepIndex !== undefined
      ? stateStepIndex
      : getStepIndex(progressSteps, "Generate Page Objects");

  // Helper function to get route path based on step name
  const getStepPath = (stepName) => {
    const stepPaths = {
      "Test Case Imported":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
      "Introduce SUT":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/introduce-sut",
      "Generate Locators":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators",
      "Generate Page Objects":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-page-objects",
      "Generate Test Files":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-test-files",
    };
    return (
      stepPaths[stepName] ||
      "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-page-objects"
    );
  };

  const handleBack = () => {
    navigate(
      "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators",
      {
        state: {
          automationType,
          preferences,
          environment,
          testCases,
          currentTestCaseIndex,
          totalTestCases,
          generatedLocators,
          pageSource,
        },
      }
    );
  };

  const handleNext = async () => {
    let fake;
    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 2.4;
        if (progress >= 98 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        setLoadingState((prev) => ({
          ...prev,
          progress: Math.min(progress, 98),
        }));
      }, 100);

      return {
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);
          setLoadingState((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((resolve) => setTimeout(resolve, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };

    // Initial message
    setLoadingState({
      open: true,
      message: "Generating Test Files",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      // Convert edited string content back to JSON array format for API call
      const pageObjectsArray = convertPageClassesStringToArray(editedContent);

      // Prepare data for test files generation
      const requestData = {
        pageObjects: pageObjectsArray, // Send as JSON array
        testCases: testCases,
        currentTestCaseIndex: currentTestCaseIndex,
        generatedLocators: generatedLocators,
        pageSource: pageSource,
        automationType: automationType,
        preferences: preferences,
        environment: environment,
      };

      console.log(
        `📤 Sending ${pageObjectsArray.length} page objects to test files API`
      );

      // Call the Playwright TypeScript Test Files API
      const response = await playwrightTypeScriptTestFilesAPI(requestData);
      const generatedTestFiles = response.data;

      await finishTo100AndClose();

      const nextStep = getNextStep(progressSteps, currentStepIndex);
      if (nextStep) {
        const nextStepPath = getStepPath(nextStep);
        // Convert edited content back to JSON array format for passing to next page
        const pageObjectsArray = convertPageClassesStringToArray(editedContent);

        navigate(nextStepPath, {
          state: {
            automationType,
            preferences,
            environment,
            testCases,
            currentTestCaseIndex,
            totalTestCases,
            currentStep: nextStep,
            currentStepIndex: currentStepIndex + 1,
            generatedLocators,
            generatedPageObjects: pageObjectsArray, // Pass as JSON array
            generatedTestFiles,
            pageSource,
            projectId, // Pass the project ID for project generation
          },
        });
      }
    } catch (error) {
      console.error("Error generating test files:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        error?.response?.data?.message ||
        "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setLoadingState({ open: false, message: "", progress: null });

      // Still navigate to next page even if API fails
      const nextStep = getNextStep(progressSteps, currentStepIndex);
      if (nextStep) {
        const nextStepPath = getStepPath(nextStep);
        // Convert edited content back to JSON array format for passing to next page
        const pageObjectsArray = convertPageClassesStringToArray(editedContent);

        navigate(nextStepPath, {
          state: {
            automationType,
            preferences,
            environment,
            testCases,
            currentTestCaseIndex,
            totalTestCases,
            currentStep: nextStep,
            currentStepIndex: currentStepIndex + 1,
            generatedLocators,
            generatedPageObjects: pageObjectsArray, // Pass as JSON array
            pageSource,
          },
        });
      }
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    // Reset to original generated content or no data message
    let contentToUse;
    if (generatedPageObjects && isValidPageClassesArray(generatedPageObjects)) {
      // Convert JSON array back to string format
      contentToUse = convertPageClassesArrayToString(generatedPageObjects);
    } else {
      // Use legacy string format or no data message
      contentToUse =
        generatedPageObjects ||
        "// No page objects generated yet. Please generate page objects first.";
    }
    setEditedContent(contentToUse);
    setIsEditing(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(editedContent);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([editedContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "page-objects.feature";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleUploadTestData = () => {
    setUploadDrawerOpen(true);
  };

  const handleUploadSuccess = (uploadedFiles, uploadedProjectId) => {
    setPopupMessage(
      `Successfully uploaded ${uploadedFiles.length} test data file(s) for project ${uploadedProjectId}`
    );
    setPopupOpen(true);
    // Verify the project ID matches our session project ID
    if (uploadedProjectId !== projectId) {
      console.warn("⚠️ Project ID mismatch:", {
        session: projectId,
        upload: uploadedProjectId,
      });
    }
    console.log("✅ Upload successful for session project ID:", projectId);
  };

  return (
    <>
      <MessagePopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
      />
      <Box
        sx={{
          minHeight: "100vh",
          background: "#F6F3F2",
          display: "flex",
          flexDirection: "column",
          py: 4,
        }}
      >
        <Container maxWidth="lg" sx={{ width: "100%", flex: 1 }}>
          {/* Custom Stepper */}
          <Box sx={{ mb: 4 }}>
            <CustomStepper
              steps={progressSteps}
              activeStep={currentStepIndex}
              maxWidth="100%"
              containerWidth="100%"
              activeColor="#1976d2"
              completedColor="#40a9ff"
              pendingColor="#d1d1d1"
              showNextStep={true}
              showStepCount={true}
              title="Now"
              inProgressText="(In Progress)"
              completedText="(Completed)"
              nextText="Next"
              completedNextText="Completed"
              stepText="Step"
            />
          </Box>

          {/* Page Title */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mb: { xs: 2, sm: 3, md: 4 },
              fontWeight: "bold",
              color: "#333",
              fontSize: { xs: "20px", sm: "24px", md: "28px" },
              fontFamily: "Manrope",
              lineHeight: { xs: 1.3, sm: 1.4 },
            }}
          >
            Page Class
          </Typography>

          {/* Main Content Card */}
          <Card
            sx={{
              backgroundColor: "white",
              borderRadius: { xs: "4px", sm: "8px" },
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              mb: { xs: 2, sm: 3, md: 4 },
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              {/* Click to edit label */}
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  mb: 2,
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  cursor: isEditing ? "default" : "pointer",
                  "&:hover": {
                    color: isEditing ? "#666" : "#1976d2",
                  },
                }}
                onClick={!isEditing ? handleEditClick : undefined}
              >
                {isEditing ? "Editing..." : "Click to edit"}
              </Typography>

              {/* Code Editor Box */}
              <Box
                sx={{
                  position: "relative",
                  backgroundColor: "#1e1e1e",
                  borderRadius: "8px",
                  border: "1px solid #d1d1d1",
                  minHeight: "400px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  lineHeight: 1.5,
                  overflow: "hidden",
                }}
              >
                {/* Copy to clipboard button */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 1,
                  }}
                >
                  <Tooltip title={isCopied ? "Copied!" : "Copy to clipboard"}>
                    <Button
                      size="small"
                      onClick={handleCopyToClipboard}
                      startIcon={<CopyIcon />}
                      sx={{
                        color: "#1976d2",
                        fontSize: "12px",
                        textTransform: "none",
                        minWidth: "auto",
                        p: 1,
                      }}
                    >
                      Copy to clipboard
                    </Button>
                  </Tooltip>
                </Box>

                {/* Edit mode buttons */}
                {isEditing && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 140,
                      zIndex: 1,
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Button
                      size="small"
                      onClick={handleSaveEdit}
                      sx={{
                        color: "#4caf50",
                        fontSize: "12px",
                        textTransform: "none",
                        minWidth: "auto",
                        p: 1,
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        color: "#f44336",
                        fontSize: "12px",
                        textTransform: "none",
                        minWidth: "auto",
                        p: 1,
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}

                {/* Code content */}
                {isEditing ? (
                  <Editor
                    height="400px"
                    defaultLanguage="typescript"
                    value={editedContent}
                    onChange={(value) => setEditedContent(value || "")}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 14,
                      fontFamily: "monospace",
                      lineNumbers: "on",
                      wordWrap: "on",
                      automaticLayout: true,
                      padding: { top: 16, bottom: 16 },
                      cursorStyle: "line",
                      selectOnLineNumbers: true,
                      roundedSelection: false,
                      readOnly: false,
                      contextmenu: true,
                      mouseWheelZoom: true,
                      smoothScrolling: true,
                      cursorBlinking: "blink",
                      cursorSmoothCaretAnimation: true,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      padding: "16px",
                      color: "#ffffff",
                      fontFamily: "monospace",
                      fontSize: "14px",
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      overflow: "auto",
                      cursor: "pointer",
                      minHeight: "400px",
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                    onClick={handleEditClick}
                  >
                    {editedContent}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Footer Navigation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "transparent",
              borderRadius: "0 0 8px 8px",
            }}
          >
            {/* Left: Back Button */}
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
              sx={{
                width: 120,
                height: 40,
                border: "2px solid #0094FB",
                borderRadius: 2,
                backgroundColor: "transparent",
                padding: "8px 16px",
                fontFamily: "Manrope",
                fontWeight: 700,
                fontSize: 20,
                lineHeight: "24px",
                letterSpacing: 0.15,
                color: "#0094FB",
                textTransform: "none",
                "&:hover": {
                  border: "2px solid #0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
                transition: "all 0.3s ease-out",
                marginRight: 37,
              }}
            >
              Back
            </Button>

            {/* Center: Upload Test Data Button */}
            <Button
              variant="outlined"
              startIcon={<UploadBlueIcon />}
              onClick={handleUploadTestData}
              sx={{
                width: 240,
                height: 40,
                borderRadius: 2,
                border: "2px solid #0094FB",
                padding: "8px 16px",
                fontFamily: "Manrope",
                fontWeight: 700,
                fontSize: 20,
                lineHeight: "24px",
                letterSpacing: 0.15,
                color: "#0094FB",
                textTransform: "none",
                "&:hover": {
                  border: "2px solid #0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
                transition: "all 0.3s ease-out",
              }}
            >
              Upload test data
            </Button>

            {/* Right: Download and Generate Test Files Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Download Button */}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                sx={{
                  width: 200,
                  height: 40,
                  border: "2px solid #0094FB",
                  borderRadius: 2,
                  backgroundColor: "transparent",
                  padding: "8px 16px",
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "24px",
                  letterSpacing: 0.15,
                  color: "#0094FB",
                  textTransform: "none",
                  "&:hover": {
                    border: "2px solid #0094FB",
                    backgroundColor: "rgba(0, 148, 251, 0.04)",
                  },
                  transition: "all 0.3s ease-out",
                }}
              >
                Download File
              </Button>

              {/* Next Button */}
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                sx={{
                  width: 246,
                  height: 40,
                  backgroundColor: "#0094FB",
                  borderRadius: 2,
                  padding: "8px 16px",
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "24px",
                  letterSpacing: 0.15,
                  color: "white",
                  textTransform: "none",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#007ACC",
                  },
                  transition: "all 0.3s ease-out",
                }}
              >
                Generate Test Files
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Upload Test Data Drawer */}
      <UploadTestDataDrawer
        open={uploadDrawerOpen}
        onClose={() => setUploadDrawerOpen(false)}
        environment={environment}
        onUploadSuccess={handleUploadSuccess}
        projectId={projectId}
      />
    </>
  );
};

export default GeneratePageObjectsPage;
